/**
 * Maps an animation step to the inline CSS that plays it.
 *
 * Kept separate from animationUtils (which stays DOM-free and fully unit-testable) because
 * this module encodes presentation concerns: keyframe names, easing, and the transform
 * origins each direction implies. The keyframes themselves live in animations.css.
 */

/** Keyframe name for an effect/direction pair. Must match animations.css. */
const KEYFRAMES = {
  appear: () => 'msAnimAppear',
  fade: () => 'msAnimFade',
  zoom: () => 'msAnimZoom',
  flyIn: (dir) => ({
    fromBottom: 'msAnimFlyInBottom',
    fromTop: 'msAnimFlyInTop',
    fromLeft: 'msAnimFlyInLeft',
    fromRight: 'msAnimFlyInRight',
  }[dir] || 'msAnimFlyInBottom'),
  wipe: (dir) => ({
    fromBottom: 'msAnimWipeBottom',
    fromTop: 'msAnimWipeTop',
    fromLeft: 'msAnimWipeLeft',
    fromRight: 'msAnimWipeRight',
  }[dir] || 'msAnimWipeBottom'),
  split: (dir) => ({
    horizontalOut: 'msAnimSplitHOut',
    horizontalIn: 'msAnimSplitHIn',
    verticalOut: 'msAnimSplitVOut',
    verticalIn: 'msAnimSplitVIn',
  }[dir] || 'msAnimSplitHOut'),
  box: (dir) => (dir === 'in' ? 'msAnimBoxIn' : 'msAnimBoxOut'),
  circle: () => 'msAnimCircle',
  stretch: (dir) => ({
    fromLeft: 'msAnimStretchLeft',
    fromRight: 'msAnimStretchRight',
    fromTop: 'msAnimStretchTop',
    fromBottom: 'msAnimStretchBottom',
    fromCenter: 'msAnimStretchCenter',
  }[dir] || 'msAnimStretchLeft'),
  growTurn: () => 'msAnimGrowTurn',
  floatIn: (dir) => (dir === 'fromTop' ? 'msAnimFloatDown' : 'msAnimFloatUp'),
  bounce: () => 'msAnimBounce',
  swivel: (dir) => (dir === 'vertical' ? 'msAnimSwivelVertical' : 'msAnimSwivelHorizontal'),
};

/** Appear is instantaneous in PowerPoint regardless of the duration set on it. */
const INSTANT_EFFECTS = new Set(['appear']);

const SOLID = 'linear-gradient(#000, #000)';
const maskLayers = (n) => Array(n).fill(SOLID).join(', ');
const noRepeat = (n) => Array(n).fill('no-repeat').join(', ');

/**
 * Effects whose reveal has a hole in it — bands growing inward from opposite edges — cannot
 * be expressed with clip-path, so they use a mask of solid bands whose sizes the keyframes
 * animate. Split In needs two bands; Box In needs four. Every other effect needs no base
 * declaration, and the distinctness test asserts exactly that.
 */
const MASK_BASE = {
  msAnimSplitHIn: {
    WebkitMaskImage: maskLayers(2), maskImage: maskLayers(2),
    WebkitMaskRepeat: noRepeat(2), maskRepeat: noRepeat(2),
    WebkitMaskPosition: 'top, bottom', maskPosition: 'top, bottom',
  },
  msAnimSplitVIn: {
    WebkitMaskImage: maskLayers(2), maskImage: maskLayers(2),
    WebkitMaskRepeat: noRepeat(2), maskRepeat: noRepeat(2),
    WebkitMaskPosition: 'left, right', maskPosition: 'left, right',
  },
  msAnimBoxIn: {
    WebkitMaskImage: maskLayers(4), maskImage: maskLayers(4),
    WebkitMaskRepeat: noRepeat(4), maskRepeat: noRepeat(4),
    WebkitMaskPosition: 'top, bottom, left, right', maskPosition: 'top, bottom, left, right',
  },
};

/**
 * Inline style for an object at a given playback state.
 *
 *   'hidden'  - animated but not yet reached; occupies no visual space
 *   'playing' - currently running its keyframe, offset by `startOffset`
 *   'shown'   - already played, or never animated
 *
 * Returns a plain style object suitable for spreading onto an element's `style`.
 */
export const animationStyle = (state, step) => {
  if (state === 'shown' || !step) return {};
  if (state === 'hidden') return { visibility: 'hidden' };

  const keyframeFor = KEYFRAMES[step.effect] || KEYFRAMES.fade;
  const name = keyframeFor(step.direction);
  const duration = INSTANT_EFFECTS.has(step.effect) ? 1 : step.duration;

  return {
    ...(MASK_BASE[name] || {}),
    animationName: name,
    animationDuration: `${duration}ms`,
    animationDelay: `${step.startOffset || 0}ms`,
    animationTimingFunction: step.effect === 'flyIn' ? 'cubic-bezier(0.22, 0.61, 0.36, 1)' : 'ease-out',
    animationFillMode: 'both',
    animationIterationCount: 1,
  };
};

/** Total time (ms) before a scheduled step has finished painting. */
export const stepEndTime = (step) => (step.startOffset || 0) + (step.duration || 0);
